"use client";

import { useCallback, useRef, useState, type MutableRefObject } from "react";
import { useOtpAutoSubmit } from "@/components/auth/useOtpAutoSubmit";
type OtpChallengeStage = "idle" | "code_sent" | "verified" | "unavailable";

/** What a failed request answered — the surface turns it into its own wording. */
interface OtpFailure {
  status: number;
  /** The server's structured `code`, e.g. "landline_not_supported". */
  code?: string;
  /** The server's own message, when it sent one. */
  error?: string;
}
export interface UseOtpChallengeOptions {
  /** Server truth that the challenge is already passed; re-synced when it changes. */
  verified?: boolean;
  /** The stage to start in — "code_sent" when an earlier step already sent the code. */
  initialStage?: OtpChallengeStage;
  /**
   * Sends (or re-sends) the code. Omit when an earlier step sent it and a
   * resend is not this challenge's to make (`send` then does nothing).
   */
  requestCode?: () => Promise<Response>;
  /** Checks the code. */
  submitCode: (code: string) => Promise<Response>;
  /** Wording for a failed send; defaults to the server's message or a generic one. */
  describeSendFailure?: (failure: OtpFailure) => string;
  /** Wording for a refused code; defaults to the server's message or a generic one. */
  describeVerifyFailure?: (failure: OtpFailure) => string;
  onCodeSent?: (data: Record<string, unknown>) => void;
  onVerified?: (data: Record<string, unknown>) => void;
  /** Check the code as soon as all six digits are in. */
  autoSubmit?: boolean;
}
export interface OtpChallenge {
  stage: OtpChallengeStage;
  code: string[];
  setCode: (code: string[]) => void;
  error: string | null;
  busy: boolean;
  inputsRef: MutableRefObject<(HTMLInputElement | null)[]>;
  send: () => Promise<void>;
  verify: () => Promise<void>;
  /** Forget a verdict about an old value: clear error and code, back to the start stage. */
  reset: () => void;
}
const EMPTY_CODE = () => Array<string>(6).fill("");
const NETWORK_ERROR = "Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.";
const defaultSendFailure = (f: OtpFailure) => f.error || "Der Code konnte nicht gesendet werden. Bitte versuchen Sie es erneut.";
const defaultVerifyFailure = (f: OtpFailure) => f.error || "Der Code konnte nicht bestätigt werden. Bitte prüfen Sie die Eingabe oder fordern Sie einen neuen Code an.";
async function readJson(res: Response): Promise<Record<string, unknown>> {
  const data: unknown = await res.json().catch(() => ({}));
  return data && typeof data === "object" ? data as Record<string, unknown> : {};
}
function failureOf(res: Response, data: Record<string, unknown>): OtpFailure {
  return {
    status: res.status,
    code: typeof data.code === "string" ? data.code : undefined,
    error: typeof data.error === "string" ? data.error : undefined
  };
}

/**
 * One SMS/e-mail code challenge: send, enter six digits, check. Shared by the
 * Ergänzen callback-number confirmation and the profile's login-phone change,
 * which runs it twice (step-up code, then the new number's code). A 503 means
 * the channel is unavailable, not that the user did something wrong.
 *
 * Every failure reaches `error`, including a failed SEND: Ergänzen once kept
 * its only error display inside the "code sent" panel, so a refused send
 * re-enabled the button and showed nothing.
 */
export function useOtpChallenge(options: UseOtpChallengeOptions): OtpChallenge {
  const {
    verified = false,
    requestCode,
    submitCode,
    describeSendFailure = defaultSendFailure,
    describeVerifyFailure = defaultVerifyFailure,
    onCodeSent,
    onVerified,
    autoSubmit = false
  } = options;
  const startStage = options.initialStage ?? (verified ? "verified" : "idle");
  const [stage, setStage] = useState<OtpChallengeStage>(startStage);
  const [code, setCode] = useState<string[]>(EMPTY_CODE);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  // Keep the stage in step with server truth when it reports the challenge as
  // passed. Taken in the render that sees the change (React's "adjust state
  // while rendering"), not in an effect after it.
  const [verifiedSeen, setVerifiedSeen] = useState(verified);
  if (verified !== verifiedSeen) {
    setVerifiedSeen(verified);
    if (verified) setStage("verified");
  }
  const reset = useCallback(() => {
    setError(null);
    setStage(options.initialStage ?? (verified ? "verified" : "idle"));
    setCode(EMPTY_CODE());
  }, [options.initialStage, verified]);
  const send = useCallback(async () => {
    if (!requestCode) return;
    setBusy(true);
    setError(null);
    try {
      const res = await requestCode();
      if (res.status === 503) {
        setStage("unavailable");
        return;
      }
      const data = await readJson(res);
      if (!res.ok) {
        setError(describeSendFailure(failureOf(res, data)));
        return;
      }
      setStage("code_sent");
      setCode(EMPTY_CODE());
      onCodeSent?.(data);
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setBusy(false);
    }
  }, [requestCode, describeSendFailure, onCodeSent]);
  const verify = useCallback(async () => {
    if (!code.every(Boolean)) return;
    setBusy(true);
    setError(null);
    try {
      const res_0 = await submitCode(code.join(""));
      const data_0 = await readJson(res_0);
      if (!res_0.ok) {
        setError(describeVerifyFailure(failureOf(res_0, data_0)));
        setCode(EMPTY_CODE());
        return;
      }
      setStage("verified");
      onVerified?.(data_0);
    } catch {
      setError(NETWORK_ERROR);
    } finally {
      setBusy(false);
    }
  }, [code, submitCode, describeVerifyFailure, onVerified]);
  useOtpAutoSubmit({
    code,
    isReady: autoSubmit && stage === "code_sent",
    isLoading: busy,
    onSubmit: verify
  });
  return {
    stage,
    code,
    setCode,
    error,
    busy,
    inputsRef,
    send,
    verify,
    reset
  };
}